import React, { useState } from 'react';
import { Settings, Mail, Download, LogOut, Copy, ArrowLeft } from 'lucide-react';
import ApiService from '../services/ApiService';

function SubscriptionPaywallView({ onUpdateSubscription, onLogout, onShowToast }) {
  const [loading, setLoading] = useState(false);
  const [backupUrls, setBackupUrls] = useState(null);

  const handleDownloadRecordings = async () => {
    setLoading(true);
    try {
      const urls = await ApiService.getBackupUrls();
      if (!urls || urls.length === 0) {
        onShowToast?.('No recordings available to download', 'error');
        setLoading(false);
        return;
      }
      setBackupUrls(urls);
    } catch (error) {
      onShowToast?.(error.message || 'Failed to get download links', 'error');
    }
    setLoading(false);
  };

  const handleCopyAll = () => {
    if (!backupUrls) return;
    navigator.clipboard.writeText(backupUrls.join('\n\n'))
      .then(() => onShowToast?.('All URLs copied to clipboard', 'success'))
      .catch(() => onShowToast?.('Failed to copy to clipboard', 'error'));
  };

  if (backupUrls) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex flex-col p-6">
        <div className="max-w-2xl w-full mx-auto space-y-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setBackupUrls(null)}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <button
              onClick={handleCopyAll}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white font-medium transition-colors"
            >
              <Copy className="w-4 h-4" />
              Copy All
            </button>
          </div>

          <h2 className="text-xl font-bold">Download Links ({backupUrls.length})</h2>
          <p className="text-gray-400 text-sm">Copy the links below to download your recordings. Links expire in 48 hours.</p>

          <div className="space-y-2">
            {backupUrls.map((url, i) => (
              <a
                key={i}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="block p-3 bg-gray-800 hover:bg-gray-700 rounded-lg text-purple-400 text-sm break-all transition-colors"
              >
                Recording {i + 1}
              </a>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col items-center justify-center p-6">
      <div className="max-w-sm w-full text-center space-y-6">
        <div>
          <h1 className="text-3xl font-bold mb-2">Subscription Paused</h1>
          <p className="text-gray-400">
            Please resume your subscription or update your payment method.
          </p>
        </div>

        <div className="flex justify-center py-4">
          <Settings className="w-20 h-20 text-purple-400 opacity-60" />
        </div>

        <div className="space-y-3 w-full">
          <button
            onClick={onUpdateSubscription}
            className="w-full py-3.5 bg-purple-600 hover:bg-purple-700 rounded-xl text-white font-medium transition-colors"
          >
            Update Subscription
          </button>

          <button
            onClick={handleDownloadRecordings}
            disabled={loading}
            className="flex items-center justify-center gap-2 w-full py-3.5 bg-gray-800 hover:bg-gray-700 rounded-xl text-white font-medium transition-colors disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            {loading ? 'Getting Links...' : 'Download Recordings'}
          </button>

          <a
            href="mailto:support@membox.app"
            className="flex items-center justify-center gap-2 w-full py-3.5 bg-gray-800 hover:bg-gray-700 rounded-xl text-white font-medium transition-colors"
          >
            <Mail className="w-4 h-4" />
            Contact Support
          </a>

          <button
            onClick={onLogout}
            className="flex items-center justify-center gap-2 w-full py-3.5 bg-gray-800 hover:bg-gray-700 rounded-xl text-white font-medium transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}

export default SubscriptionPaywallView;
